// src/store/slices/relapseSlice.ts - Relapse tracking state management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { firestore } from '../../firebase/config';

// Define types
interface RelapseIncident {
  id?: string;
  userId: string;
  type: string;
  description: string;
  notes?: string;
  triggers?: string[];
  timestamp: number;
}

interface TriggerTag {
  id?: string;
  userId: string;
  name: string;
  description?: string;
  color?: string;
}

interface RelapseState {
  incidents: RelapseIncident[];
  triggers: TriggerTag[];
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: RelapseState = {
  incidents: [],
  triggers: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchRelapseIncidents = createAsyncThunk(
  'relapse/fetchRelapseIncidents',
  async (userId: string, { rejectWithValue }) => {
    try {
      const incidentsRef = collection(firestore, 'relapseIncidents');
      const q = query(
        incidentsRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const incidents: RelapseIncident[] = [];
      
      querySnapshot.forEach((doc) => {
        incidents.push({
          id: doc.id,
          ...doc.data() as RelapseIncident,
        });
      });
      
      return incidents;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRelapseIncident = createAsyncThunk(
  'relapse/createRelapseIncident',
  async (incidentData: Partial<RelapseIncident>, { rejectWithValue }) => {
    try {
      if (!incidentData.userId || !incidentData.type || !incidentData.description) {
        return rejectWithValue('User ID, type, and description are required');
      }
      
      const incidentsRef = collection(firestore, 'relapseIncidents');
      
      const newIncident: RelapseIncident = {
        userId: incidentData.userId,
        type: incidentData.type,
        description: incidentData.description,
        notes: incidentData.notes || '',
        triggers: incidentData.triggers || [],
        timestamp: incidentData.timestamp || Date.now(),
      };
      
      const docRef = await addDoc(incidentsRef, newIncident);
      
      return {
        id: docRef.id,
        ...newIncident,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRelapseIncident = createAsyncThunk(
  'relapse/updateRelapseIncident',
  async ({ userId, incidentId, updates }: { userId: string; incidentId: string; updates: Partial<RelapseIncident> }, { rejectWithValue }) => {
    try {
      const incidentRef = doc(firestore, 'relapseIncidents', incidentId);
      
      await updateDoc(incidentRef, updates);
      
      return {
        id: incidentId,
        ...updates,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRelapseIncident = createAsyncThunk(
  'relapse/deleteRelapseIncident',
  async ({ userId, incidentId }: { userId: string; incidentId: string }, { rejectWithValue }) => {
    try {
      const incidentRef = doc(firestore, 'relapseIncidents', incidentId);
      
      await deleteDoc(incidentRef);
      
      return incidentId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTriggerTags = createAsyncThunk(
  'relapse/fetchTriggerTags',
  async (userId: string, { rejectWithValue }) => {
    try {
      const triggersRef = collection(firestore, 'triggerTags');
      const q = query(
        triggersRef,
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const triggers: TriggerTag[] = [];
      
      querySnapshot.forEach((doc) => {
        triggers.push({
          id: doc.id,
          ...doc.data() as TriggerTag,
        });
      });
      
      return triggers;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createTriggerTag = createAsyncThunk(
  'relapse/createTriggerTag',
  async (tagData: Partial<TriggerTag>, { rejectWithValue }) => {
    try {
      if (!tagData.userId || !tagData.name) {
        return rejectWithValue('User ID and name are required');
      }
      
      const triggersRef = collection(firestore, 'triggerTags');
      
      const newTag: TriggerTag = {
        userId: tagData.userId,
        name: tagData.name,
        description: tagData.description || '',
        color: tagData.color || '#3F51B5',
      };
      
      const docRef = await addDoc(triggersRef, newTag);
      
      return {
        id: docRef.id,
        ...newTag,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateTriggerTag = createAsyncThunk(
  'relapse/updateTriggerTag',
  async ({ userId, tagId, updates }: { userId: string; tagId: string; updates: Partial<TriggerTag> }, { rejectWithValue }) => {
    try {
      const tagRef = doc(firestore, 'triggerTags', tagId);
      
      await updateDoc(tagRef, updates);
      
      return {
        id: tagId,
        ...updates,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTriggerTag = createAsyncThunk(
  'relapse/deleteTriggerTag',
  async ({ userId, tagId }: { userId: string; tagId: string }, { rejectWithValue }) => {
    try {
      const tagRef = doc(firestore, 'triggerTags', tagId);
      
      await deleteDoc(tagRef);
      
      return tagId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create slice
const relapseSlice = createSlice({
  name: 'relapse',
  initialState,
  reducers: {
    clearRelapseData: (state) => {
      state.incidents = [];
      state.triggers = [];
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch relapse incidents
    builder.addCase(fetchRelapseIncidents.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRelapseIncidents.fulfilled, (state, action) => {
      state.isLoading = false;
      state.incidents = action.payload;
    });
    builder.addCase(fetchRelapseIncidents.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create relapse incident
    builder.addCase(createRelapseIncident.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createRelapseIncident.fulfilled, (state, action) => {
      state.isLoading = false;
      state.incidents = [action.payload, ...state.incidents];
    });
    builder.addCase(createRelapseIncident.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Update relapse incident
    builder.addCase(updateRelapseIncident.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateRelapseIncident.fulfilled, (state, action) => {
      state.isLoading = false;
      state.incidents = state.incidents.map(incident => 
        incident.id === action.payload.id 
          ? { ...incident, ...action.payload } 
          : incident
      );
    });
    builder.addCase(updateRelapseIncident.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Delete relapse incident
    builder.addCase(deleteRelapseIncident.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteRelapseIncident.fulfilled, (state, action) => {
      state.isLoading = false;
      state.incidents = state.incidents.filter(incident => incident.id !== action.payload);
    });
    builder.addCase(deleteRelapseIncident.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Fetch trigger tags
    builder.addCase(fetchTriggerTags.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTriggerTags.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers = action.payload;
    });
    builder.addCase(fetchTriggerTags.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create trigger tag
    builder.addCase(createTriggerTag.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createTriggerTag.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers = [...state.triggers, action.payload];
    });
    builder.addCase(createTriggerTag.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Update trigger tag
    builder.addCase(updateTriggerTag.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTriggerTag.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers = state.triggers.map(trigger => 
        trigger.id === action.payload.id 
          ? { ...trigger, ...action.payload } 
          : trigger
      );
    });
    builder.addCase(updateTriggerTag.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Delete trigger tag
    builder.addCase(deleteTriggerTag.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteTriggerTag.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers = state.triggers.filter(trigger => trigger.id !== action.payload);
    });
    builder.addCase(deleteTriggerTag.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearRelapseData } = relapseSlice.actions;
export default relapseSlice.reducer;
